import { useTheme } from '@shopify/restyle';
import { Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, isToday, isTomorrow, isYesterday } from 'date-fns';
import { View } from 'react-native';
import type { LoanStatus } from 'app/types/loan';
import { getLoanETA, LoanETA } from 'app/types/loan';
import { localeAwareFormat } from 'app/lib/date';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import type { Theme } from 'app/shopify';
import { IconClock } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isDonationListing } from 'app/lib/listing';

export function LoanStatusRow({
  start,
  end,
  returned,
  status,
  showClock,
  listingSubtype,
  isDonation: listingIsDonation,
  transactionMode,
}: {
  start: string;
  end: string;
  returned?: string;
  status: LoanStatus;
  showClock: boolean;
  listingSubtype: ListingSubtype;
  isDonation?: boolean;
  transactionMode?: TransactionMode | null;
}) {
  const { t } = useTranslation('loans');
  const theme = useTheme<Theme>();

  const { loanStatus, loanStatusColor, loanStatusTextColor, calculatedText } = (() => {
    const loanStart = new Date(start);
    const loanEnd = new Date(end);
    const loanReturned = returned ? new Date(returned) : undefined;

    const config = {
      [LoanETA.pending]: {
        date: loanStart,
        bgColor: theme.colors['surface.inverse.tertiary-neutral'],
        textColor: theme.colors['text.primary'],
      },
      [LoanETA.scheduled]: {
        date: loanStart,
        bgColor: theme.colors['surface.brand.secondary-2.default'],
        textColor: theme.colors['text.primary'],
      },
      [LoanETA.ongoing]: {
        date: loanEnd,
        bgColor: theme.colors['surface.brand.secondary-1.default'],
        textColor: theme.colors['text.primary'],
      },
      [LoanETA.late]: {
        date: loanEnd,
        bgColor: theme.colors['surface.brand.secondary-4.default'],
        textColor: theme.colors['text.primary'],
      },
      [LoanETA.returned]: {
        date: loanReturned ?? loanEnd,
        bgColor: theme.colors['surface.brand.secondary-2.default'],
        textColor: theme.colors['text.primary'],
      },
      [LoanETA.past]: {
        date: loanReturned ?? loanEnd,
        bgColor: theme.colors['surface.inverse.tertiary-neutral'],
        textColor: theme.colors['text.primary'],
      },
    };

    const _loanStatus = getLoanETA({ start, end, status });
    const loanDate = config[_loanStatus].date;

    const isService = listingSubtype === ListingSubtype.service;
    const isSell = transactionMode === TransactionMode.sell;
    const isDonation = isDonationListing({
      isDonation: listingIsDonation,
      subtype: listingSubtype,
      transactionMode,
    });
    const tPrefix = isSell ? 'whenSale' : isService ? 'whenService' : isDonation ? 'whenDonation' : 'when';

    let tKey = `card.${tPrefix}.${_loanStatus}.`;
    if (isToday(loanDate)) {
      tKey += 'today';
    } else if (
      (_loanStatus === LoanETA.late || _loanStatus === LoanETA.past || _loanStatus === LoanETA.returned) &&
      isYesterday(loanDate)
    ) {
      tKey += 'yesterday';
    } else if (isTomorrow(loanDate)) {
      tKey += 'tomorrow';
    } else {
      tKey += 'x_days';
    }

    const _localizedDate = localeAwareFormat(new Date(loanDate), t('dateFormat_MMMdYYYY', { ns: 'common' }));

    const _numDays = Math.abs(differenceInCalendarDays(new Date(), loanDate));

    return {
      loanStatus:
        transactionMode === TransactionMode.sell && _loanStatus === LoanETA.returned
          ? 'returnedSale'
          : isService && _loanStatus === LoanETA.returned
            ? 'returnedService'
            : isDonation && _loanStatus === LoanETA.returned
              ? 'returnedDonation'
              : _loanStatus,
      loanStatusColor: config[_loanStatus].bgColor,
      loanStatusTextColor: config[_loanStatus].textColor,
      calculatedText: t(tKey, { numDays: _numDays, date: _localizedDate }),
    };
  })();

  return (
    <Row alignItems="center" style={{ flexDirection: 'row', flex: 1 }}>
      {showClock ? (
        <View style={{ marginRight: theme.spacing['spacing.general.x1'] }}>
          <IconClock strokeWidth={ICON_STROKE_WIDTH} />
        </View>
      ) : null}

      <Text>{calculatedText}</Text>

      <View
        style={{
          marginLeft: 'auto',
          borderRadius: theme.borderRadii['radius.sm'],
          backgroundColor: loanStatusColor,
          paddingHorizontal: theme.spacing['spacing.general.x2'],
          paddingVertical: theme.spacing['spacing.general.x1'],
        }}
      >
        <Text style={{ color: loanStatusTextColor }}>{t(`card.status.${loanStatus}`)}</Text>
      </View>
    </Row>
  );
}
